import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  ListItem,
  Spinner,
  Stack,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';

import { type CellContext, createColumnHelper } from '@tanstack/react-table';

import {
  UserAddOutline,
  CheckOutline,
  CustomAlert,
  shadowStyles,
} from '@aslanpay/portal-common-lib';

import SummaryTable from 'app/components/UI/Table/SummaryTable';

import useFetchPayCalendarByFriendlyId from 'app/hooks/useFetchPayCalendarByFriendlyId';
import { useConfirmAETFile } from 'app/hooks/useAETFileHooks';
import { useConfirmEDFFile } from 'app/hooks/useEDFFileHooks';

import { FAILED_UPLOAD_STATUSES, FileUploadStatus } from 'app/constants/fileUploads';

import { useImportEmployeesStore } from 'app/store';

import LeaveWithoutImportModal from '../LeaveWithoutImportModal';
import UploadFinishedModal from '../UploadFinishedModal';

import { ConfirmDataStepProps, AETFileSummaryData } from '../types';

import SalaryAppliesDateCell from './SalaryAppliesDateCell';

import type {
  EdfUploadAnalysisResponsePayload,
  GiftingEdfUploadAnalysisResponsePayload,
  IndividualResponsePayload,
  IndividualChangeResponsePayload,
  GiftingIndividualChangeResponsePayload,
  SalaryChangeResponsePayload,
} from 'app/types/fileProcessingService';

// Type guard for the AET summary data
const isAETFileSummary = (summary: unknown): summary is AETFileSummaryData => {
  return !!(
    summary &&
    typeof summary === 'object' &&
    'totalEmployees' in summary &&
    'newEarningsCount' in summary
  );
};

const renderSalaryAppliesDateCell = (
  info: CellContext<SalaryChangeResponsePayload, string | undefined>
) => <SalaryAppliesDateCell change={info.row.original} appliesDate={info.getValue()} />;

const renderFutureSalaryAppliesDateCell = (
  info: CellContext<SalaryChangeResponsePayload, string | undefined>
) => (
  <SalaryAppliesDateCell
    change={info.row.original}
    appliesDate={info.getValue()}
    testIdPrefix="future-salary-correction-row"
    isFutureSalaryChange
  />
);

function ConfirmData({
  onStepAction,
  onBack, // TODO: need to rethink this logic
  isLeaveWithoutImportOpen,
  onCloseLeaveWithoutImport,
}: ConfirmDataStepProps) {
  // Date formatting function to convert dates to dd/mm/yyyy format
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';

    try {
      // Handle different date formats that might come from the backend
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        // If it's already in dd/mm/yyyy format, return as is
        return dateString;
      }

      // Convert to dd/mm/yyyy format
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      // If parsing fails, return the original string
      return dateString;
    }
  };

  const { importEmployeesData } = useImportEmployeesStore.getState();

  // Get file type for context-aware behavior
  const fileType = importEmployeesData.fileType || 'EMPLOYEE_DATA_FILE';
  const isEDF = fileType === 'EMPLOYEE_DATA_FILE' || fileType === 'GIFTING_ONLY_EMPLOYEE_DATA_FILE';
  const isGiftingEDF = fileType === 'GIFTING_ONLY_EMPLOYEE_DATA_FILE';
  const isAET = fileType === 'APPROVED_EARNINGS_TIMESHEET';

  const {
    isOpen: isUploadFinishedOpen,
    onOpen: onOpenUploadFinished,
    onClose: onCloseUploadFinished,
  } = useDisclosure();

  // Transform uploadedAETFile data to AETFileSummaryData format
  const transformAETDataToSummary = (aetData: unknown): AETFileSummaryData | null => {
    if (!aetData || typeof aetData !== 'object' || aetData === null) {
      return null;
    }

    const data = aetData as { metadata?: unknown; entries?: unknown };
    if (!data.metadata || !data.entries) {
      return null;
    }

    const { metadata, entries } = data;

    // Type assertions for the expected structure
    const typedMetadata = metadata as {
      employeeEarningsAdded?: string[];
      employeeEarningsChanged?: string[];
      employeeEarningsDeleted?: string[];
      payCycleStartDate?: string;
      payCycleEndDate?: string;
    };
    const typedEntries = entries as Array<{ payday?: string }>;

    // Calculate total unique employees by combining all employee ID arrays and removing duplicates
    const allEmployeeIds = new Set([
      ...(typedMetadata.employeeEarningsAdded || []),
      ...(typedMetadata.employeeEarningsChanged || []),
      ...(typedMetadata.employeeEarningsDeleted || []),
    ]);
    const totalUniqueEmployees = allEmployeeIds.size;

    return {
      totalEmployees: totalUniqueEmployees,
      newEarningsCount: typedMetadata.employeeEarningsAdded?.length || 0,
      changedEarningsCount: typedMetadata.employeeEarningsChanged?.length || 0,
      payPeriod: {
        startDate: typedMetadata.payCycleStartDate || '',
        endDate: typedMetadata.payCycleEndDate || '',
      },
      payday: typedEntries[0]?.payday || '', // Get payday from first entry
      metadata: {
        employeeEarningsDeleted: typedMetadata.employeeEarningsDeleted || [],
      },
    };
  };

  const aetFileSummary = transformAETDataToSummary(importEmployeesData.uploadedAETFile);

  // EDF (and gifting EDF) analysis data, produced by fps at the analyse step (see useUploadEDFFile)
  const edfAnalysisData = !isGiftingEDF
    ? (importEmployeesData.uploadedEDFFile?.analysis as
        | EdfUploadAnalysisResponsePayload
        | undefined)
    : undefined;
  const giftingEdfAnalysisData = isGiftingEDF
    ? (importEmployeesData.uploadedEDFFile?.analysis as
        | GiftingEdfUploadAnalysisResponsePayload
        | undefined)
    : undefined;

  // Extract pay calendar friendly ID from AET file for lookup
  const payCalendarFriendlyId =
    importEmployeesData.uploadedAETFile?.metadata?.payCalendarFriendlyId;

  // Fetch pay calendar by friendly ID for AET files
  const { data: payCalendarData, isLoading: isLoadingPayCalendar } =
    useFetchPayCalendarByFriendlyId(payCalendarFriendlyId, isAET && !!payCalendarFriendlyId);

  const isLoadingSummary = false;

  const { mutate: confirmEDFFile, isLoading: isConfirmingEDFFile } = useConfirmEDFFile({
    onSuccess: () => {
      onOpenUploadFinished();
    },
  });

  const { mutate: confirmAETFile, isLoading: isConfirmingAETFile } = useConfirmAETFile({
    onSuccess: () => {
      onOpenUploadFinished();
    },
  });

  const isConfirming = isAET ? isConfirmingAETFile : isConfirmingEDFFile;

  const handleImport = (sendInvites = false) => {
    if (!importEmployeesData.bulkImportId) return;

    if (isAET) {
      confirmAETFile({ fileUploadId: importEmployeesData.bulkImportId });
    } else if (isEDF) {
      confirmEDFFile({ fileUploadId: importEmployeesData.bulkImportId, sendInvites });
    }
  };

  const renderSummaryErrorAlert = (title: string) => {
    return (
      <>
        <HStack spacing="50px">
          <CustomAlert variant="simple" status="error" title={title} />
          <Button flexShrink={0} onClick={onBack}>
            {isAET ? 'Re-upload timesheet file' : 'Re-upload CSV file'}
          </Button>
        </HStack>
        <Flex
          flexDir="column"
          p="20px"
          gap="20px"
          borderRadius="10px"
          border="1px solid"
          borderColor="brand.lightGray1"
          flexGrow={1}
        >
          <Text textStyle="smallSemiBold" color="brand.danger">
            {isAET ? 'Timesheet Processing Error' : 'File Processing Error'}
          </Text>
          <Text textStyle="body" color="brand.mutedPurple">
            {isAET
              ? 'Please ensure your timesheet file has the correct format with 3 metadata rows followed by the data rows.'
              : 'Please ensure your employee data file matches the template structure and try again.'}
          </Text>
          <LeaveWithoutImportModal
            isImporting={isConfirming}
            isOpen={isLeaveWithoutImportOpen}
            onClose={onCloseLeaveWithoutImport}
            onImport={handleImport}
            onBack={onStepAction}
          />
        </Flex>
      </>
    );
  };

  const renderSummaryNotPresent = () => (
    <Stack spacing="50px">
      <CustomAlert
        variant="simple"
        status="error"
        title="Something went wrong with your bulk import. Please try again."
      />
      <Button alignSelf="center" onClick={onBack}>
        Re-upload CSV file
      </Button>
    </Stack>
  );

  const renderSummaryWithErrors = () => {
    return renderSummaryErrorAlert(
      isAET
        ? 'There was an error processing your timesheet file. Please check the file format and try again.'
        : 'There was an error processing your employee data file. Please check the file format and try again.'
    );
  };

  const renderEDFSummary = () => {
    if (!edfAnalysisData) {
      return renderSummaryNotPresent();
    }

    const groups = edfAnalysisData.payCalendarGroups ?? [];
    const allJoiners = groups.flatMap((g) => g.joiners ?? []);
    // rehires isn't in the published fps client typings yet, same stand-in as primaryEmailChanges
    // below. Returning employees are reported separately from joiners because confirming the file
    // links each one to the employment it replaces rather than creating an unconnected record.
    const allRehires = groups.flatMap(
      (g) => (g as { rehires?: IndividualResponsePayload[] }).rehires ?? []
    );
    const allSalaryChanges: SalaryChangeResponsePayload[] = groups.flatMap(
      (g) => g.salaryChanges ?? []
    );
    const allNameChanges = groups.flatMap((g) => g.nameChanges ?? []);
    const allPrimaryEmailChanges = groups.flatMap(
      (g) =>
        (g as { primaryEmailChanges?: IndividualChangeResponsePayload[] }).primaryEmailChanges ?? []
    );
    const allSecondaryEmailChanges = groups.flatMap((g) => g.secondaryEmailChanges ?? []);
    const allLeaverChanges = groups.flatMap((g) => g.leaverChanges ?? []);
    const allFutureSalaryChanges: SalaryChangeResponsePayload[] = groups.flatMap(
      (g) => g.futureGrossSalaryChanges ?? []
    );
    const salaryCorrectionCount = new Set(
      [...allSalaryChanges, ...allFutureSalaryChanges]
        .filter((change) => change.uploadEntry?.salaryCorrection)
        .map((change) => change.individual?.id ?? change.uploadEntry?.payrollId)
    ).size;
    const allProbationDateChanges = groups.flatMap((g) => g.probationEndDateChanges ?? []);
    const allStartDateChanges = groups.flatMap((g) => g.startDateChanges ?? []);
    const allJobTitleChanges = groups.flatMap((g) => g.jobTitleChanges ?? []);
    const allOtherChanges = groups.flatMap((g) => g.otherChanges ?? []);
    const allGroupingChanges = groups.flatMap((g) => g.groupingChanges ?? []);
    const allPayrollIdChanges = groups.flatMap((g) => g.payrollIdChanges ?? []);

    const { hasChanges } = edfAnalysisData;

    const joinerColHelper = createColumnHelper<IndividualResponsePayload>();
    const joinerColumns = [
      joinerColHelper.accessor('payrollId', { header: 'Payroll ID', enableSorting: false }),
      joinerColHelper.accessor('firstName', { header: 'First Name', enableSorting: false }),
      joinerColHelper.accessor('lastName', { header: 'Last Name', enableSorting: false }),
      joinerColHelper.accessor('email', { header: 'Email', enableSorting: false }),
      joinerColHelper.accessor('startDate', { header: 'Start Date', enableSorting: false }),
      joinerColHelper.accessor('grossSalary', { header: 'Gross Salary', enableSorting: false }),
    ];

    const changeColHelper = createColumnHelper<IndividualChangeResponsePayload>();
    const baseChangeColumns = [
      changeColHelper.accessor((row) => row.individual?.payrollId, {
        id: 'payrollId',
        header: 'Payroll ID',
        enableSorting: false,
      }),
      changeColHelper.accessor((row) => row.individual?.firstName, {
        id: 'firstName',
        header: 'First Name',
        enableSorting: false,
      }),
      changeColHelper.accessor((row) => row.individual?.lastName, {
        id: 'lastName',
        header: 'Last Name',
        enableSorting: false,
      }),
      changeColHelper.accessor((row) => row.changeInfo?.previousValue, {
        id: 'previousValue',
        header: 'Previous Value',
        enableSorting: false,
      }),
      changeColHelper.accessor((row) => row.changeInfo?.newValue, {
        id: 'newValue',
        header: 'New Value',
        enableSorting: false,
      }),
    ];
    const salaryChangeColHelper = createColumnHelper<SalaryChangeResponsePayload>();
    const salaryChangeColumns = [
      ...baseChangeColumns,
      salaryChangeColHelper.accessor((row) => row.uploadEntry?.salaryAppliesDate, {
        id: 'salaryAppliesDate',
        header: 'Applies From',
        enableSorting: false,
        cell: renderSalaryAppliesDateCell,
      }),
    ];
    const futureSalaryChangeColumns = [
      ...baseChangeColumns,
      salaryChangeColHelper.accessor((row) => row.uploadEntry?.futureSalaryAppliesDate, {
        id: 'futureSalaryAppliesDate',
        header: 'Applies From',
        enableSorting: false,
        cell: renderFutureSalaryAppliesDateCell,
      }),
    ];
    const leaverColumns = [
      changeColHelper.accessor((row) => row.individual?.payrollId, {
        id: 'payrollId',
        header: 'Payroll ID',
        enableSorting: false,
      }),
      changeColHelper.accessor((row) => row.individual?.firstName, {
        id: 'firstName',
        header: 'First Name',
        enableSorting: false,
      }),
      changeColHelper.accessor((row) => row.individual?.lastName, {
        id: 'lastName',
        header: 'Last Name',
        enableSorting: false,
      }),
      changeColHelper.accessor((row) => row.uploadEntry?.leaveDate, {
        id: 'leaveDate',
        header: 'Leave Date',
        enableSorting: false,
      }),
    ];

    return (
      <>
        <CustomAlert
          variant="simple"
          status="success"
          title={<Text textStyle="h6SemiBold">{hasChanges ? 'File okay' : 'No changes'}</Text>}
        />
        <Flex
          flexDir="column"
          maxW={hasChanges ? 'full' : '572px'}
          w="full"
          alignSelf="center"
          borderRadius="10px"
          boxShadow={shadowStyles.dropdownMenu}
        >
          <HStack
            px="40px"
            py="20px"
            borderBottom="1px solid"
            borderColor="brand.lightGray1"
            alignItems="center"
            justify="space-between"
          >
            <Text as="h5" textStyle="h5Bold" color="brand.black">
              {`${hasChanges ? 'Confirm your' : 'No'} changes to ${
                importEmployeesData.payCalendarName
              }`}
            </Text>
          </HStack>

          <Flex flexDir="column" px="40px" py="20px" gap="20px">
            <Text as="h6" textStyle="h6Medium" color="brand.mutedPurple">
              Are you sure this information is correct?
            </Text>
            {salaryCorrectionCount > 0 && (
              <Box data-testid="salary-correction-summary-warning">
                <CustomAlert
                  variant="simple"
                  status="warning"
                  title={
                    salaryCorrectionCount === 1
                      ? '1 employee has a salary that applies from a past date. Any salary changes after that date will be replaced.'
                      : `${salaryCorrectionCount} employees have a salary that applies from a past date. Any salary changes after that date will be replaced.`
                  }
                />
              </Box>
            )}
            <UnorderedList spacing="10px" color="brand.purple" px="10px">
              {[
                {
                  id: 'joiners',
                  count: allJoiners.length,
                  text:
                    allJoiners.length === 1
                      ? 'new employee has been added'
                      : 'new employees have been added',
                },
                {
                  id: 'rehires',
                  count: allRehires.length,
                  text: allRehires.length === 1 ? 'employee was rehired' : 'employees were rehired',
                },
                {
                  id: 'salary',
                  count: allSalaryChanges.length,
                  text:
                    allSalaryChanges.length === 1
                      ? 'employee had a salary change'
                      : 'employees had salary changes',
                },
                {
                  id: 'name',
                  count: allNameChanges.length,
                  text:
                    allNameChanges.length === 1
                      ? 'employee had a name change'
                      : 'employees had name changes',
                },
                {
                  id: 'primaryEmail',
                  count: allPrimaryEmailChanges.length,
                  text:
                    allPrimaryEmailChanges.length === 1
                      ? 'employee had a primary email change'
                      : 'employees had primary email changes',
                },
                {
                  id: 'secondaryEmail',
                  count: allSecondaryEmailChanges.length,
                  text:
                    allSecondaryEmailChanges.length === 1
                      ? 'employee had a secondary email change'
                      : 'employees had secondary email changes',
                },
                {
                  id: 'leavers',
                  count: allLeaverChanges.length,
                  text:
                    allLeaverChanges.length === 1
                      ? 'employee has been marked as leaving'
                      : 'employees have been marked as leaving',
                },
                {
                  id: 'futureSalary',
                  count: allFutureSalaryChanges.length,
                  text:
                    allFutureSalaryChanges.length === 1
                      ? 'employee had a future salary change'
                      : 'employees had future salary changes',
                },
                {
                  id: 'probation',
                  count: allProbationDateChanges.length,
                  text:
                    allProbationDateChanges.length === 1
                      ? 'employee had a probation end date change'
                      : 'employees had probation end date changes',
                },
                {
                  id: 'startDate',
                  count: allStartDateChanges.length,
                  text:
                    allStartDateChanges.length === 1
                      ? 'employee had a start date change'
                      : 'employees had start date changes',
                },
                {
                  id: 'jobTitle',
                  count: allJobTitleChanges.length,
                  text:
                    allJobTitleChanges.length === 1
                      ? 'employee had a job title change'
                      : 'employees had job title changes',
                },
                {
                  id: 'grouping',
                  count: allGroupingChanges.length,
                  text:
                    allGroupingChanges.length === 1
                      ? 'employee had a grouping change'
                      : 'employees had grouping changes',
                },
                {
                  id: 'payrollId',
                  count: allPayrollIdChanges.length,
                  text:
                    allPayrollIdChanges.length === 1
                      ? 'employee had a payroll ID change'
                      : 'employees had payroll ID changes',
                },
                {
                  id: 'other',
                  count: allOtherChanges.length,
                  text:
                    allOtherChanges.length === 1
                      ? 'employee had other changes'
                      : 'employees had other changes',
                },
              ].map(({ id, count, text }) => (
                <ListItem key={id} textStyle="h6SemiBold">
                  {count}{' '}
                  <Text as="span" color="brand.mutedPurple">
                    {text}
                  </Text>
                </ListItem>
              ))}
            </UnorderedList>

            {hasChanges && (
              <SummaryTable
                sections={[
                  { title: 'New joiners', columns: joinerColumns, data: allJoiners },
                  { title: 'Rehires', columns: joinerColumns, data: allRehires },
                  {
                    title: 'Salary changes',
                    columns: salaryChangeColumns,
                    data: allSalaryChanges,
                  },
                  { title: 'Name changes', columns: baseChangeColumns, data: allNameChanges },
                  {
                    title: 'Primary email changes',
                    columns: baseChangeColumns,
                    data: allPrimaryEmailChanges,
                  },
                  {
                    title: 'Secondary email changes',
                    columns: baseChangeColumns,
                    data: allSecondaryEmailChanges,
                  },
                  { title: 'Leavers', columns: leaverColumns, data: allLeaverChanges },
                  {
                    title: 'Future salary changes',
                    columns: futureSalaryChangeColumns,
                    data: allFutureSalaryChanges,
                  },
                  {
                    title: 'Probation end date changes',
                    columns: baseChangeColumns,
                    data: allProbationDateChanges,
                  },
                  {
                    title: 'Start date changes',
                    columns: baseChangeColumns,
                    data: allStartDateChanges,
                  },
                  {
                    title: 'Job title changes',
                    columns: baseChangeColumns,
                    data: allJobTitleChanges,
                  },
                  {
                    title: 'Grouping changes',
                    columns: baseChangeColumns,
                    data: allGroupingChanges,
                  },
                  {
                    title: 'Payroll ID changes',
                    columns: baseChangeColumns,
                    data: allPayrollIdChanges,
                  },
                  { title: 'Other changes', columns: baseChangeColumns, data: allOtherChanges },
                ]}
              />
            )}

            <Text textStyle="extraSmallMedium" color="brand.darkGray">
              Hey, we want to make sure you&apos;re confident about the changes to your employee
              data. Once you&apos;ve completed this step, it&apos;s final! If you&apos;ve got any
              questions or need a hand before hitting the import button, don&apos;t hesitate to
              reach out to the Aslan support team.
            </Text>
            <HStack justify="center" spacing="25px">
              <Button
                size="sm"
                variant="outline"
                rightIcon={<CheckOutline boxSize="20px" />}
                onClick={() => handleImport()}
                isLoading={isConfirmingEDFFile}
                isDisabled={!hasChanges}
              >
                Import
              </Button>
              <Button
                size="sm"
                rightIcon={<UserAddOutline boxSize="20px" />}
                onClick={() => handleImport(true)}
                isLoading={isConfirmingEDFFile}
                isDisabled={!hasChanges}
              >
                Import & invite
              </Button>
            </HStack>
          </Flex>
          <LeaveWithoutImportModal
            isImporting={isConfirmingEDFFile}
            hasChanges={hasChanges}
            isOpen={isLeaveWithoutImportOpen}
            onClose={onCloseLeaveWithoutImport}
            onImport={handleImport}
            onBack={onStepAction}
          />
        </Flex>
      </>
    );
  };

  const renderGiftingEdfSummary = () => {
    if (!giftingEdfAnalysisData) {
      return renderSummaryNotPresent();
    }

    const { joiners, nameChanges, startDateChanges, leaverChanges, hasChanges } =
      giftingEdfAnalysisData;

    const changeColHelper = createColumnHelper<GiftingIndividualChangeResponsePayload>();
    const changeColumns = [
      changeColHelper.accessor('email', { header: 'Email', enableSorting: false }),
      changeColHelper.accessor('previousValue', {
        header: 'Previous Value',
        enableSorting: false,
        cell: (info) => info.getValue() || '-',
      }),
      changeColHelper.accessor('newValue', {
        header: 'New Value',
        enableSorting: false,
        cell: (info) => info.getValue() || '-',
      }),
    ];

    const joinerColHelper = createColumnHelper<NonNullable<typeof joiners>[number]>();
    const joinerColumns = [
      joinerColHelper.accessor('firstName', { header: 'First Name', enableSorting: false }),
      joinerColHelper.accessor('lastName', { header: 'Last Name', enableSorting: false }),
      joinerColHelper.accessor('email', { header: 'Email', enableSorting: false }),
      joinerColHelper.accessor('startDate', { header: 'Start Date', enableSorting: false }),
    ];

    const summaryCounts = [
      {
        id: 'joiners',
        count: joiners?.length || 0,
        text: joiners?.length === 1 ? 'new person will be added' : 'new people will be added',
      },
      {
        id: 'nameChanges',
        count: nameChanges?.length || 0,
        text: nameChanges?.length === 1 ? 'person had a name change' : 'people had name changes',
      },
      {
        id: 'startDateChanges',
        count: startDateChanges?.length || 0,
        text:
          startDateChanges?.length === 1
            ? 'person had a start date change'
            : 'people had start date changes',
      },
      {
        id: 'leaverChanges',
        count: leaverChanges?.length || 0,
        text:
          leaverChanges?.length === 1
            ? 'person had a leave date change'
            : 'people had leave date changes',
      },
    ];

    return (
      <>
        <CustomAlert
          variant="simple"
          status="success"
          title={<Text textStyle="h6SemiBold">{hasChanges ? 'File okay' : 'No changes'}</Text>}
        />
        <Flex
          flexDir="column"
          maxW={hasChanges ? 'full' : '572px'}
          w="full"
          alignSelf="center"
          borderRadius="10px"
          boxShadow={shadowStyles.dropdownMenu}
        >
          <HStack
            px="40px"
            py="20px"
            borderBottom="1px solid"
            borderColor="brand.lightGray1"
            alignItems="center"
            justify="space-between"
          >
            <Text as="h5" textStyle="h5Bold" color="brand.black">
              {hasChanges ? 'Confirm your data' : 'No changes'}
            </Text>
          </HStack>

          <Flex flexDir="column" px="40px" py="20px" gap="20px">
            <Text as="h6" textStyle="h6Medium" color="brand.mutedPurple">
              Are you sure this information is correct?
            </Text>
            <UnorderedList spacing="10px" color="brand.purple" px="10px">
              {summaryCounts.map(({ id, count, text }) => (
                <ListItem key={id} textStyle="h6SemiBold">
                  {count}{' '}
                  <Text as="span" color="brand.mutedPurple">
                    {text}
                  </Text>
                </ListItem>
              ))}
            </UnorderedList>

            {hasChanges && (
              <SummaryTable
                sections={[
                  { title: 'New joiners', columns: joinerColumns, data: joiners || [] },
                  { title: 'Name changes', columns: changeColumns, data: nameChanges || [] },
                  {
                    title: 'Start date changes',
                    columns: changeColumns,
                    data: startDateChanges || [],
                  },
                  {
                    title: 'Leave date changes',
                    columns: changeColumns,
                    data: leaverChanges || [],
                  },
                ]}
              />
            )}

            <Text textStyle="extraSmallMedium" color="brand.darkGray">
              Almost there! Once you complete this step, your data changes will be saved, but you
              can always update them later. Need help before importing? Reach out to the Aslan
              support team.
            </Text>
            <HStack justify="center" spacing="25px">
              <Button
                size="sm"
                rightIcon={<CheckOutline boxSize="20px" />}
                onClick={() => handleImport()}
                isLoading={isConfirmingEDFFile}
                isDisabled={!hasChanges}
              >
                Import
              </Button>
            </HStack>
          </Flex>
          <LeaveWithoutImportModal
            isImporting={isConfirmingEDFFile}
            hasChanges={hasChanges}
            isOpen={isLeaveWithoutImportOpen}
            onClose={onCloseLeaveWithoutImport}
            onImport={handleImport}
            onBack={onStepAction}
          />
        </Flex>
      </>
    );
  };

  const renderAETSummary = () => {
    // Only AET files have these properties
    if (!isAET || !aetFileSummary) {
      return <Text>No AET summary data available</Text>;
    }

    if (!isAETFileSummary(aetFileSummary)) {
      return <Text>Invalid AET summary data</Text>;
    }

    const { newEarningsCount, changedEarningsCount, payPeriod, payday } = aetFileSummary;

    // Calculate additional counts for AET summary
    const deletedEarningsCount = aetFileSummary?.metadata?.employeeEarningsDeleted?.length || 0;

    const summaryCounts = [
      {
        id: 'newEarningsCount',
        count: newEarningsCount,
        text:
          newEarningsCount === 1
            ? 'employee has new approved earnings'
            : 'employees have new approved earnings',
      },
      {
        id: 'changedEarningsCount',
        count: changedEarningsCount,
        text:
          changedEarningsCount === 1
            ? 'employee had changes to their approved earnings'
            : 'employees had changes to their approved earnings',
      },
      {
        id: 'deletedEarningsCount',
        count: deletedEarningsCount,
        text:
          deletedEarningsCount === 1
            ? 'employee had deleted earnings'
            : 'employees had deleted earnings',
      },
      {
        id: 'periodStart',
        count: null,
        text: `${payPeriod.startDate} is the period start date`,
      },
      {
        id: 'periodEnd',
        count: null,
        text: `${payPeriod.endDate} is the period end date`,
      },
    ].filter((item) => item.count === null || item.count > 0); // Only show items with counts > 0 or period dates

    const hasChanges = summaryCounts.reduce((acc, current) => acc + (current.count || 0), 0) > 0;

    return (
      <>
        <CustomAlert
          variant="simple"
          status="success"
          title={<Text textStyle="h6SemiBold">Timesheet okay</Text>}
        />
        <Flex
          flexDir="column"
          maxW="572px"
          alignSelf="center"
          borderRadius="10px"
          boxShadow={shadowStyles.dropdownMenu}
        >
          <HStack
            px="40px"
            py="20px"
            borderBottom="1px solid"
            borderColor="brand.lightGray1"
            alignItems="center"
            justify="space-between"
          >
            <Text as="h5" textStyle="h5Bold" color="brand.black">
              Confirm your timesheet changes for &apos;
              {(() => {
                if (isLoadingPayCalendar) {
                  return '...';
                }
                return payCalendarData?.name || 'Unknown Timesheet';
              })()}
              &apos;
            </Text>
          </HStack>

          <Flex flexDir="column" px="40px" py="20px" gap="20px">
            <Text as="h6" textStyle="h6Medium" color="brand.mutedPurple">
              Are you sure this information is correct?
            </Text>

            <UnorderedList spacing="10px" color="brand.purple" px="10px">
              {!hasChanges ? (
                <>
                  <ListItem textStyle="h6SemiBold">
                    <Text as="span" color="brand.purple">
                      0
                    </Text>
                    <Text as="span" color="brand.mutedPurple">
                      {' '}
                      changes to any employee(s)
                    </Text>
                  </ListItem>
                  <ListItem textStyle="h6SemiBold" color="brand.mutedPurple">
                    <Text as="span" color="brand.purple">
                      {formatDate(payPeriod.startDate)}
                    </Text>{' '}
                    is the period start date
                  </ListItem>
                  <ListItem textStyle="h6SemiBold" color="brand.mutedPurple">
                    <Text as="span" color="brand.purple">
                      {formatDate(payPeriod.endDate)}
                    </Text>{' '}
                    is the period end date
                  </ListItem>
                  <ListItem textStyle="h6SemiBold" color="brand.mutedPurple">
                    <Text as="span" color="brand.purple">
                      {formatDate(payday)}
                    </Text>{' '}
                    is the pay day
                  </ListItem>
                </>
              ) : (
                summaryCounts.map(({ id, count, text }) => (
                  <ListItem key={id} textStyle="h6SemiBold">
                    {count !== null && `${count} `}
                    <Text as="span" color="brand.mutedPurple">
                      {text}
                    </Text>
                  </ListItem>
                ))
              )}
            </UnorderedList>
            <Text textStyle="extraSmallMedium" color="brand.darkGray">
              Hey, we want to make sure you&apos;re confident about the changes to your timesheet
              data. Once you&apos;ve completed this step, it&apos;s final! If you&apos;ve got any
              questions or need a hand before hitting the confirm button, don&apos;t hesitate to
              reach out to the Aslan support team.
            </Text>
            <Flex justify="center">
              <Button
                size="sm"
                rightIcon={<UserAddOutline boxSize="20px" />}
                onClick={() => handleImport()}
                isLoading={isConfirmingAETFile}
                isDisabled={!hasChanges}
                width="180px"
                height="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="8px"
              >
                Confirm upload
              </Button>
            </Flex>
          </Flex>
          <LeaveWithoutImportModal
            isImporting={isConfirmingAETFile}
            hasChanges={hasChanges}
            isOpen={isLeaveWithoutImportOpen}
            onClose={onCloseLeaveWithoutImport}
            onImport={handleImport}
            onBack={onStepAction}
          />
        </Flex>
      </>
    );
  };

  const renderSummaryPresent = () => {
    if (isAET) {
      return renderAETSummary();
    }

    if (isGiftingEDF) {
      return renderGiftingEdfSummary();
    }

    return renderEDFSummary();
  };

  // bulkImportStatus is set from real fps statuses (see UploadFile) — 'ERROR' is a legacy
  // company-manager status that fps never emits, so checking it alone let failed fps uploads
  // render the non-error confirmation UI instead of the error state.
  const hasErrors = FAILED_UPLOAD_STATUSES.includes(
    importEmployeesData.bulkImportStatus as FileUploadStatus
  );
  const hasAnalysisData = isAET
    ? !!aetFileSummary
    : !!importEmployeesData.uploadedEDFFile?.analysis;

  const renderSummary = () => {
    if (!hasAnalysisData) return renderSummaryNotPresent();
    if (hasErrors) return renderSummaryWithErrors();
    return renderSummaryPresent();
  };

  return isLoadingSummary ? (
    <Center width="full" height="full" flexGrow={1}>
      <Spinner color="brand.purple" size="xl" />
    </Center>
  ) : (
    <Flex flexDir="column" w="full" gap="40px" flexGrow={1}>
      {renderSummary()}
      <UploadFinishedModal isOpen={isUploadFinishedOpen} onClose={onCloseUploadFinished} />
    </Flex>
  );
}

export default ConfirmData;
